/* eslint-disable max-lines */
import { AxiosError, AxiosResponse } from 'axios';
import {
  Dispatch,
  JSX,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import {
  BuildertrendLinkApi,
  BuildertrendUnlinkApi,
} from '../api/b2b/buildertrend';
import {
  GetLocalDeliveriesAPI,
  GetOrderHistoryAPI,
  qbCountAPI,
} from '../api/oms/order-history';
import { GetBigReceiptAPI } from '../api/oms/receipt';
import { FeatureSwitches } from '../constants/feature-switches';
import { ERR_GENERIC } from '../constants/response-error-messages';
import { IAlert } from '../models/common';
import {
  IBtApiState,
  ILDRequest,
  IOrderHistoryModalHooks,
  IOrderHistoryResponse,
  IPHRequest,
} from '../models/purchase-history';
import { pushErrorEvent } from '../utils/analytics';
import { createTimeStamp } from '../utils/date-utils';
import { openFile, parseBlob } from '../utils/http-helper';
import {
  setNoRecordsFoundError,
  setPurchaseHistoryPageError,
} from '../utils/purchase-history/analytics';
import { getSvocId, getUserId } from '../utils/user';
import { useExperienceContext } from './useContexts';
import {
  CPS_ORDER_HISTORY_CACHE_MAX_ENTRIES,
  CPS_ORDER_HISTORY_CACHE_TTL_MS,
  CpsOrderHistoryCache,
} from '../models/cps-order-history-cache';

interface IBuildertrendError {
  errors: { errorCode: string; errorMessage: string }[];
}

export const OrderHistoryPoJobNameHooks = () => {
  const [poJobNameState, setPoJobNameState] = useState('');

  return { poJobNameState, setPoJobNameState };
};

const cpsOrderHistoryCache = new CpsOrderHistoryCache(
  CPS_ORDER_HISTORY_CACHE_MAX_ENTRIES,
  CPS_ORDER_HISTORY_CACHE_TTL_MS,
);

export const OrderHistoryModalHooks = (): IOrderHistoryModalHooks => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [orderHistoryModalComponent, setOrderHistoryModalComponent] =
    useState<JSX.Element | null>(null);

  return {
    openModal,
    setOpenModal,
    orderHistoryModalComponent,
    setOrderHistoryModalComponent,
  };
};

export const GetLocalDeliveryData = (requestData: ILDRequest) => {
  const { userID } = useThdCustomer() || {};
  const [localDeliveryData, setLocalDeliveryData] = useState(undefined);
  const [localDeliveryError, setLocalDeliveryError] = useState<
    AxiosError | undefined
  >(undefined);
  const [localDeliveryLoaded, setLocalDeliveryLoaded] = useState(false);

  const wrappedRequest = { orderHistoryRequest: requestData };

  useEffect(() => {
    GetLocalDeliveriesAPI(wrappedRequest, userID)
      .then((response) => {
        if (response.data.orderCount === 0) setNoRecordsFoundError();
        setLocalDeliveryData(response.data);
      })
      .catch((error: AxiosError) => {
        // setPurchaseHistoryPageError(error);
        setLocalDeliveryError(error);
      })
      .finally(() => {
        setLocalDeliveryLoaded(true);
      });

    return () => {
      setLocalDeliveryLoaded(false);
    };
  }, [requestData.startDate, requestData.endDate, requestData.pageNumber]);

  return {
    localDeliveryData,
    localDeliveryError,
    localDeliveryLoaded,
  };
};

export const GetOrderHistoryData = (
  requestData: IPHRequest,
  enabled = true,
) => {
  const { channel } = useExperienceContext();
  const { userID } = useThdCustomer() || {};
  const previousUserIdRef = useRef<string | undefined>(userID);
  const [orderHistoryData, setOrderHistoryData] = useState<
    IOrderHistoryResponse | undefined
  >(undefined);
  const [orderHistoryError, setOrderHistoryError] = useState<
    AxiosError | undefined
  >(undefined);
  const [orderHistoryLoaded, setOrderHistoryLoaded] = useState(false);
  const [cpsDataSource, setCPSDataSource] = useState<string | undefined>(
    undefined,
  );

  const wrappedRequest = { orderHistoryRequest: requestData };

  useEffect(() => {
    if (previousUserIdRef.current && previousUserIdRef.current !== userID) {
      cpsOrderHistoryCache.clear();
    }

    previousUserIdRef.current = userID;
  }, [userID]);

  useEffect(() => {
    const isCpsSearch = Boolean(requestData.searchValue?.trim());
    const cacheKey = JSON.stringify({
      userID,
      channel,
      startDate: requestData.startDate,
      endDate: requestData.endDate,
      pageNumber: requestData.pageNumber,
      pageSize: requestData.pageSize,
      resultsFilter: requestData.resultsFilter,
      sortBy: requestData.sortBy,
      sortOrder: requestData.sortOrder,
      searchBy: requestData.searchBy,
      searchValue: requestData.searchValue?.trim(),
    });

    if (isCpsSearch) {
      const cachedEntry = cpsOrderHistoryCache.get(cacheKey);
      const isCacheValid =
        Boolean(cachedEntry) &&
        Date.now() - (cachedEntry?.createdAt ?? 0) <
          CPS_ORDER_HISTORY_CACHE_TTL_MS;

      if (isCacheValid && cachedEntry) {
        setOrderHistoryData(cachedEntry.data);
        setOrderHistoryError(undefined);
        setCPSDataSource(cachedEntry.cpsDataSource);
        setOrderHistoryLoaded(true);
        return () => {
          setOrderHistoryError(undefined);
          setOrderHistoryLoaded(false);
        };
      }

      if (cachedEntry && !isCacheValid) {
        cpsOrderHistoryCache.delete(cacheKey);
      }
    }

    if (!enabled) return undefined;

    setOrderHistoryLoaded(false);
    GetOrderHistoryAPI(wrappedRequest, channel, userID)
      .then((response: AxiosResponse<IOrderHistoryResponse>) => {
        if (response.data.orderCount === 0) setNoRecordsFoundError();
        setOrderHistoryData(response.data);
        setCPSDataSource(response.headers.datasource);
        if (isCpsSearch) {
          cpsOrderHistoryCache.set(cacheKey, {
            data: response.data,
            cpsDataSource: response.headers.datasource,
            createdAt: Date.now(),
          });
        }
      })
      .catch((error: AxiosError) => {
        setPurchaseHistoryPageError(error);
        setOrderHistoryError(error);
        setCPSDataSource(error?.response?.headers?.datasource);
      })
      .finally(() => {
        setOrderHistoryLoaded(true);
      });

    return () => {
      setOrderHistoryError(undefined);
      setOrderHistoryLoaded(false);
    };
  }, [
    userID,
    channel,
    requestData.startDate,
    requestData.endDate,
    requestData.pageNumber,
    requestData.sortBy,
    requestData.sortOrder,
    requestData.resultsFilter,
    requestData.searchBy,
    requestData.searchValue,
    enabled,
  ]);

  return {
    orderHistoryData,
    orderHistoryError,
    orderHistoryLoaded,
    cpsDataSource,
  };
};

export const GetBigReceiptHook = (
  encryptedBigReceiptRequest: string,
  setAlertState: (value: IAlert | null) => void,
) => {
  const userId = getUserId() || '';
  const svocId = getSvocId() || '';

  const encryptedRequest = {
    userId,
    customerAccountId: svocId,
    encryptedBigReceiptRequest,
  };

  const omsEncryptedRequest = {
    userId,
    customerAccountId: svocId,
    encryptedBigReceiptRequest,
  };

  const receiptEncryptedRequest = FeatureSwitches.OMS_VIEW_BIG_RECEIPT_SWITCH
    ? omsEncryptedRequest
    : encryptedRequest;

  GetBigReceiptAPI(receiptEncryptedRequest)
    .then((response: AxiosResponse) => {
      setAlertState({
        alertTitle: 'Success',
        alertMessage: `Big Receipt has been successfully exported.
      Please check your browser downloads folder for the file.`,
        status: 'success',
      });

      const blob = parseBlob(response.data, 'data:application/pdf,');
      const fileName = `Order_Invoice${createTimeStamp()}.pdf`;
      openFile(blob, fileName);
    })
    .catch((error: AxiosError) => {
      const statusCode = error.response?.status.toString() || '';
      const statusMessage = error.response?.statusText || '';
      pushErrorEvent('purchase history', statusCode, statusMessage, 'inline');
      setAlertState({
        alertTitle: 'Failed',
        alertMessage: ERR_GENERIC.message,
        status: 'error',
      });
    });
};

/**
 * Purchase History Page Header Hooks s
 */
export const ConnectBuildertrendAccount = (
  code: string,
  BtApiState: IBtApiState,
  setBtApiState: Dispatch<SetStateAction<IBtApiState>>,
) => {
  /**
   * btResponseObj - Holds the response data for either postive or negitive outcomes
   * On Postive: fed devs do not have to show any response data, just UX
   * On Negitive: fed devs need to show error in some way to user.
   */
  let btResponseObj: IBuildertrendError | { linked: boolean };

  setBtApiState({ ...BtApiState, isLoading: true });
  BuildertrendLinkApi(code)
    .then(() => {
      btResponseObj = { linked: true };
    })
    .catch((error: AxiosError<IBuildertrendError>) => {
      // Just returns the errorCode, mapper function will generate message

      /*   AnalyticsService.setErrorEvent({
        type: 'error',
        displayType: 'inline',
        pageSection: 'buildertrend',
        message: btResultMessage,
        code: btResultCode,
      }); */
      btResponseObj = error.response?.data || {
        errors: [
          {
            errorCode: 'DEFAULT',
            errorMessage:
              'We’re sorry, there was a temporary technical error linking accounts. Please close this window and retry by clicking Link Buildertrend again or contact customer support if you continue to receive this error.',
          },
        ],
      };
    })
    .finally(() =>
      setBtApiState({ isLoading: false, btResponse: btResponseObj }),
    );
};

export const DisconnectBuildertrendAccount = (
  requestType: 'customer' | 'user',
  _BtApiState: IBtApiState,
  setBtApiState: (value: IBtApiState) => void,
) => {
  /**
   * btResponseObj - Holds the response data for either postive or negitive outcomes
   * On Postive: fed devs do not have to show any response data, just UX
   * On Negitive: fed devs need to show error in some way to user.
   */
  let btResponseObj: IBuildertrendError | { unlinked: boolean };
  BuildertrendUnlinkApi(requestType)
    .then(() => {
      btResponseObj = { unlinked: true };
    })
    .catch((error: AxiosError<IBuildertrendError>) => {
      // Just returns the errorCode, mapper function will generate message

      /*   AnalyticsService.setErrorEvent({
        type: 'error',
        displayType: 'inline',
        pageSection: 'buildertrend',
        message: btResultMessage,
        code: btResultCode,
      }); */
      btResponseObj = error.response?.data || {
        errors: [
          {
            errorCode: 'DEFAULT',
            errorMessage:
              'We’re sorry, there was a temporary technical error linking accounts. Please close this window and retry by clicking Link Buildertrend again or contact customer support if you continue to receive this error.',
          },
        ],
      };
    })
    .finally(() =>
      setBtApiState({
        isLoading: false,
        btResponse: btResponseObj,
      }),
    );
};

/**
 * QuickBooks Hooks
 */
export const GetQBOrderCount = (
  request: IPHRequest,
  openModal?: boolean,
): number | undefined => {
  const { userID } = useThdCustomer() || {};
  const [qbOrderCount, setQBOrderCount] = useState<number | undefined>();
  const wrappedRequest = { orderHistoryRequest: request };
  useEffect(() => {
    if (openModal) {
      qbCountAPI(wrappedRequest, userID)
        .then((response) => {
          setQBOrderCount(response.data.orderCount);
        })
        .catch(() => {
          setQBOrderCount(undefined);
        });
    }
  }, [openModal, request.startDate, request.endDate]);

  return qbOrderCount;
};
